import { useCurrentResume } from '@/hooks/use-documents'
import { useSSEEvent } from '@/hooks/use-event-stream'
import { useQueryClient } from '@tanstack/react-query'
import { useEffect, useRef } from 'react'

const STALE_THRESHOLD_MS = 5 * 60 * 1000 // 5 minutes

function isMatchingStale(startedAt: string | null | undefined): boolean {
  if (!startedAt) return false
  const elapsed = Date.now() - new Date(startedAt).getTime()
  return elapsed > STALE_THRESHOLD_MS
}

/**
 * Hook to track document processing and job matching status.
 * Uses SSE events for real-time updates instead of polling.
 * Auto-invalidates recommended jobs when matching completes.
 */
export function useJobMatchingStatus() {
  const queryClient = useQueryClient()
  const wasMatchingRef = useRef(false)

  const { currentResume } = useCurrentResume()

  const isProcessing =
    currentResume?.status === 'uploading' || currentResume?.status === 'processing'

  const isStale =
    (currentResume?.job_matching_status === 'pending' ||
      currentResume?.job_matching_status === 'matching') &&
    isMatchingStale(currentResume?.job_matching_started_at)

  const isMatching =
    !isStale &&
    (currentResume?.job_matching_status === 'pending' ||
      currentResume?.job_matching_status === 'matching')

  const isCompleted = currentResume?.job_matching_status === 'completed'
  const isFailed = currentResume?.job_matching_status === 'failed' || isStale

  // Listen for SSE job matching progress to update counts
  useSSEEvent('job_matching_progress', () => {
    queryClient.invalidateQueries({ queryKey: ['documents'] })
  })

  // Listen for SSE job matching status changes to invalidate jobs
  useSSEEvent('job_matching_status_changed', (event) => {
    const data = event.data as { status: string }
    if (data.status === 'completed') {
      queryClient.invalidateQueries({ queryKey: ['external-jobs'] })
    }
  })

  // When matching transitions from active to completed, invalidate job queries
  useEffect(() => {
    if (isMatching) {
      wasMatchingRef.current = true
    } else if (wasMatchingRef.current && (isCompleted || isFailed)) {
      wasMatchingRef.current = false
      queryClient.invalidateQueries({ queryKey: ['external-jobs'] })
    }
  }, [isMatching, isCompleted, isFailed, queryClient])

  return {
    isProcessing,
    isMatching,
    isCompleted,
    isFailed,
    isStale,
    status: currentResume?.job_matching_status ?? null,
    resumeId: currentResume?.id,
    total: currentResume?.job_matching_total ?? null,
    completed: currentResume?.job_matching_completed_count ?? null,
  }
}
